import { layerMenuTap } from '../layer-menu-actions'
import { fetchChanges } from '../changes-last-called-actions'
import { isLayerBlocked, isLayerInMenu, isBasemapLayer } from '../../lib/layer-access'
import { isObjectCreatingTool } from '../../lib/artemis/tools'
import { moveMap, readView, viewFromInput } from '../../lib/artemis/map-view'
import superagent from 'superagent'
import { markersSet } from '../marker-actions'
import { lineDelete } from '../line-actions'
import { polygonDelete } from '../polygon-actions'
import { circleDelete } from '../circle-actions'
import { huntPlannerActiveOptions, huntPlannerArtemisApplied } from '../huntplanner-selected-options'
import { huntplannerMorefilters } from '../huntplanner-morefilters-actions'
import { resolvePlannerFilters } from '../../lib/artemis/planner'

// Artemis agent mode on web (WEB-7093). Built with ARTEMIS_AGENT=true, queries ask for
// agent mode and declare the client actions below; Artemis only uses those.

export const ARTEMIS_AGENT_ENABLED = typeof __ARTEMIS_AGENT__ !== 'undefined' && __ARTEMIS_AGENT__ === true

// Saved chats on the v3 Conversations routes (API-3641), built with ARTEMIS_CONVERSATIONS=true on top
// of agent mode. Without it the panel keeps chats for the page's lifetime only.
export const ARTEMIS_CONVERSATIONS_ENABLED = ARTEMIS_AGENT_ENABLED &&
  typeof __ARTEMIS_CONVERSATIONS__ !== 'undefined' && __ARTEMIS_CONVERSATIONS__ === true

// set_map_view (WEB-7111) is declared ahead of artemisapi offering it (API-3681): Claude is only offered
// declared actions the server implements, so an unknown name is ignored. ask_user (WEB-7134) ends the
// reply with questions; the panel shows them from the step Result, so it has no runner here.
export const ARTEMIS_CLIENT_ACTIONS = ['set_layer_visibility', 'set_map_view', 'ask_user']

// Runs set_layer_visibility as if the user had tapped the layer in the layer menu.
// Resolves with what happened, for the chat to show:
//   { outcome: 'changed' | 'unchanged' | 'not_found' | 'subscription_required' | 'invalid_input',
//     layerId, layerName, visible }
// The chat's Undo and Redo run it too, with the opposite visible.
export const runSetLayerVisibility = input => (dispatch, getState) => {
  const state = getState(),
    layerId = Number(input && input.layer_id),
    visible = !!(input && input.visible),
    layer = state.layersDictionary ? state.layersDictionary[layerId] : null,
    userSubscriptions = (state.user && state.user.userSubscription) || [],
    layerName = (layer && layer.name) || (input && input.layer_name) || `Layer ${input && input.layer_id}`,
    result = outcome => ({ outcome, layerId, layerName, visible })

  if (!layer || !isLayerInMenu(layer, userSubscriptions, state.productionLayers)) {
    console.warn('Artemis asked to change a layer the layer menu does not have:', input)
    return result('not_found')
  }

  const setting = state.userSettings.find(s => s.settingId === 1),
    active = !!setting && setting.valueArray.indexOf(layerId) !== -1

  if (active === visible) return result('unchanged')
  // A tap on a locked tile opens the upgrade prompt instead; we don't pop that up
  // unasked, the chat says why instead. Turning a locked layer off is always allowed.
  if (visible && isLayerBlocked(layer, userSubscriptions)) return result('subscription_required')
  // Tapping the active basemap swaps in the default one. Artemis is told basemaps are
  // replaced rather than turned off, so treat this as a bad request instead of guessing.
  if (!visible && isBasemapLayer(layer)) return result('invalid_input')

  dispatch(layerMenuTap(layer, { active }))
  return result('changed')
}

// Carries out the side effects of one stream event. Resolves with an action result for
// "action" events this build runs, and null otherwise.
// Runs set_map_view: moves the map to Artemis's view, and remembers where it was for Undo.
// Resolves with { outcome: 'moved' | 'invalid_input' | 'no_map', label, view, previous }.
export const runSetMapView = input => (dispatch, getState) => {
  const label = (input && typeof input.label === 'string' && input.label.trim()) || null
  const view = viewFromInput(input)
  if (!view) {
    console.warn('Artemis asked to move the map somewhere it cannot go:', input)
    return { outcome: 'invalid_input', label, view: null, previous: null }
  }
  const map = getState().mapReference && getState().mapReference()
  const previous = readView(map)
  return { outcome: moveMap(map, view) ? 'moved' : 'no_map', label, view, previous }
}

// The map Artemis's panel sits beside: Hunt Planner's own map in the planner (surface 'planner'),
// the main map everywhere else. Show on map, its overlay and Undo/Redo act on the one the user sees.
export const artemisMap = (state, surface) => {
  const ref = surface === 'planner' ? state.huntplannerMapReference : state.mapReference
  return typeof ref === 'function' ? ref() : null
}

// Undo and Redo on a map move: back to where it was, or to Artemis's view again.
export const moveArtemisMap = (view, surface) => (dispatch, getState) => moveMap(artemisMap(getState(), surface), view)

// Runs set_planner_filters (Hunt Planner guided intake): picks the state, species and hunt type
// in the planner, which then loads its units as if the user had chosen them. Resolves with
// { outcome: 'applied', selection, label } or { outcome: 'not_found', field, options }.
export const runSetPlannerFilters = input => (dispatch, getState) => {
  const result = resolvePlannerFilters(input, getState().huntplannerStatesData)
  if (result.outcome !== 'applied') {
    console.warn('Artemis picked a planner option the planner does not have:', input, result)
    return result
  }
  // More filters (method, season, draw odds…) belong to the old species and hunt type; the ones
  // Artemis gave (weapon, season, sex, points, exact points, odds) go on top. The top menu fills in the draw
  // type once the new filter values load, when Artemis didn't name one.
  const state = getState()
  const current = state.huntPlannerSelectedOptions || {}
  const changed = ['StateAbbreviation', 'Species', 'HuntTypeKey'].some(k => current[k] !== result.selection[k])
  // Weapon, season and sex (More filters) go on the same way as the draw filters.
  const draw = { ...(result.filters || {}), ...(result.draw || {}) }
  if (changed || Object.keys(draw).length) {
    const kept = changed || Array.isArray(state.huntplannerMorefilters) ? {} : { ...(state.huntplannerMorefilters || {}) }
    const next = { ...kept, ...draw }
    dispatch(huntplannerMorefilters(Object.keys(next).length ? next : []))
  }
  dispatch(huntPlannerActiveOptions(result.selection))
  dispatch(huntPlannerArtemisApplied())
  return result
}

export const handleArtemisAgentEvent = event => dispatch => {
  if (event.Type === 'action') {
    if (event.Tool === 'set_layer_visibility') {
      return Promise.resolve({ toolUseId: event.ToolUseId, tool: event.Tool, ...dispatch(runSetLayerVisibility(event.Input)) })
    }
    if (event.Tool === 'set_map_view') {
      return Promise.resolve({ toolUseId: event.ToolUseId, tool: event.Tool, ...dispatch(runSetMapView(event.Input)) })
    }
    if (event.Tool === 'set_planner_filters') {
      return Promise.resolve({ toolUseId: event.ToolUseId, tool: event.Tool, ...dispatch(runSetPlannerFilters(event.Input)) })
    }
    return Promise.resolve(null)
  }

  if (event.Type === 'tool' && event.Status === 'succeeded' && isObjectCreatingTool(event.Tool)) {
    dispatch(fetchChanges())
  }
  return Promise.resolve(null)
}

// After a reply that saved objects: sync once more, in case the refresh on the tool's
// success was skipped because a sync was already running.
export const refreshAfterArtemisReply = savedObjects => dispatch => {
  if (savedObjects) dispatch(fetchChanges())
}

export { isObjectCreatingTool }

// Saved objects (API-3638): a create_marker or create_shape step's Result is
//   { Kind: 'marker' | 'shape', Id, Name, Type, Point: [lng, lat], Bbox?: [w, s, e, n], ... }.

const SHAPE_REMOVED = { line: lineDelete, polygon: polygonDelete, circle: circleDelete }

// Deletes a saved object with the same endpoints the content menu uses, then drops it from the
// map. Written here rather than reusing markerDeleteRequest and friends, whose promises never
// settle when the request fails.
export const removeArtemisObject = result => (dispatch, getState, token) => {
  const marker = result.Kind === 'marker'
  const removed = marker ? null : SHAPE_REMOVED[result.Type]
  if (!result.Id || (!marker && !removed)) return Promise.reject(new Error(`Can't remove ${result.Kind} ${result.Type}`))
  const url = marker ? `${__API_URI_2__}/marker/delete/${result.Id}` : `${__API_URI__}/measurement/delete/${result.Id}`

  return new Promise((resolve, reject) => {
    superagent
      .delete(url)
      .set('Authorization', `Bearer ${token}`)
      .set('Platform', 'Web')
      .set('Version', `${__version__}`)
      .end(err => {
        if (err) return reject(err)
        if (marker) dispatch(markersSet((getState().markers || []).filter(m => m.id !== result.Id)))
        else dispatch(removed(result.Id))
        resolve()
      })
  })
}

// Moves the map to a saved object: its bounds for a shape, its point for a marker.
export const showArtemisObject = (result, surface) => (dispatch, getState) => {
  const map = artemisMap(getState(), surface)
  if (!map) return
  const bbox = Array.isArray(result.Bbox) && result.Bbox.length === 4 ? result.Bbox : null
  const point = Array.isArray(result.Point) && result.Point.length === 2 ? result.Point : null
  const googleMaps = window.google && window.google.maps
  const isGoogle = googleMaps && map instanceof googleMaps.Map

  if (bbox && bbox[0] !== bbox[2] && map.fitBounds) {
    const [w, s, e, n] = bbox
    if (isGoogle) map.fitBounds(new googleMaps.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e }), 60)
    else map.fitBounds([[w, s], [e, n]], { padding: 60, duration: 600 })
  } else if (point) {
    const [lng, lat] = point
    const zoom = Math.max(map.getZoom ? map.getZoom() : 0, 15)
    if (isGoogle) {
      map.panTo({ lat, lng })
      map.setZoom(zoom)
    } else if (map.flyTo) map.flyTo({ center: [lng, lat], zoom, duration: 600 })
  }
}

// Hunt data results on the map: one temporary Artemis overlay at a time, from a card's focus
//   { bbox, point, fill (GeoJSON geometry), outline (GeoJSON geometry or [w, s, e, n]), pins: [[lng, lat]] }.
// It isn't saved anywhere; clearArtemisOverlay, or the next one, takes it off.

export const OVERLAY_FILL = '#90FF00'
export const OVERLAY_LINE = '#9ECC3B'
let overlay = null

const bboxPolygon = ([w, s, e, n]) => ({ type: 'Polygon', coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] })

export const overlayFeatures = focus => {
  const features = []
  if (focus.fill) features.push({ type: 'Feature', properties: { role: 'fill' }, geometry: focus.fill })
  if (focus.outline) {
    const geometry = Array.isArray(focus.outline) ? bboxPolygon(focus.outline) : focus.outline
    features.push({ type: 'Feature', properties: { role: 'outline' }, geometry })
  }
  ;(focus.pins || []).forEach((point, i) => features.push({ type: 'Feature', properties: { role: 'pin', n: i + 1 }, geometry: { type: 'Point', coordinates: point } }))
  return { type: 'FeatureCollection', features }
}

export const clearArtemisOverlay = () => () => {
  if (!overlay) return
  try { overlay.remove() } catch (e) { /* the map went away */ }
  overlay = null
}

export const showArtemisOverlay = (focus, surface) => (dispatch, getState) => {
  dispatch(clearArtemisOverlay())
  const map = artemisMap(getState(), surface)
  if (!map || !focus) return false
  const data = overlayFeatures(focus)
  const googleMaps = window.google && window.google.maps

  if (googleMaps && map instanceof googleMaps.Map) {
    const layer = new googleMaps.Data({ map })
    layer.addGeoJson(data)
    layer.setStyle(feature => {
      const role = feature.getProperty('role')
      if (role === 'pin') {
        return { icon: { path: googleMaps.SymbolPath.CIRCLE, scale: 6, fillColor: OVERLAY_FILL, fillOpacity: 1, strokeColor: '#000', strokeWeight: 1.5 }, zIndex: 3 }
      }
      return role === 'fill'
        ? { fillColor: OVERLAY_FILL, fillOpacity: 0.28, strokeColor: OVERLAY_FILL, strokeWeight: 1, zIndex: 2, clickable: false }
        : { fillOpacity: 0, strokeColor: OVERLAY_LINE, strokeWeight: 2, zIndex: 2, clickable: false }
    })
    overlay = { remove: () => layer.setMap(null) }
  } else if (map.addSource && map.addLayer) {
    // MapLibre (the 3D map).
    const id = 'artemis-overlay'
    map.addSource(id, { type: 'geojson', data })
    map.addLayer({ id: `${id}-fill`, type: 'fill', source: id, filter: ['==', ['get', 'role'], 'fill'], paint: { 'fill-color': OVERLAY_FILL, 'fill-opacity': 0.28 } })
    map.addLayer({ id: `${id}-line`, type: 'line', source: id, filter: ['!=', ['get', 'role'], 'pin'], paint: { 'line-color': OVERLAY_LINE, 'line-width': 2 } })
    map.addLayer({ id: `${id}-pin`, type: 'circle', source: id, filter: ['==', ['get', 'role'], 'pin'], paint: { 'circle-radius': 6, 'circle-color': OVERLAY_FILL, 'circle-stroke-color': '#000', 'circle-stroke-width': 1.5 } })
    overlay = {
      remove: () => {
        ;[`${id}-pin`, `${id}-line`, `${id}-fill`].forEach(layerId => { if (map.getLayer(layerId)) map.removeLayer(layerId) })
        if (map.getSource(id)) map.removeSource(id)
      }
    }
  }

  // Move to it the way Show on a saved object does.
  dispatch(showArtemisObject({ Bbox: focus.bbox, Point: focus.point }, surface))
  return !!overlay
}
